import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link2, Trash2, Plus, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Allegati editor. Il campo `value` è un array di `{name, url, added_at}`.
 * Callback `onChange(newArray)` — nessuna chiamata API interna.
 *
 * L'utente carica il file sul suo Google Drive e incolla il link condivisibile.
 * "Carica file" apre una finestra di Drive per caricare direttamente prima di prendere il link.
 */
export default function AttachmentsField({ value = [], onChange, testId = "attachments" }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");

  const isValidUrl = (u) => {
    try { const x = new URL(u); return x.protocol === "https:" || x.protocol === "http:"; } catch { return false; }
  };

  const add = () => {
    if (!isValidUrl(url)) { toast.error("Inserisci un URL valido (https://…)"); return; }
    const label = name.trim() || (() => {
      try { const p = new URL(url).pathname.split("/").filter(Boolean); return p[p.length-1] || "Allegato"; } catch { return "Allegato"; }
    })();
    const next = [...value, { name: label, url: url.trim(), added_at: new Date().toISOString().slice(0,10) }];
    onChange(next);
    setName(""); setUrl("");
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const openDriveUpload = () => {
    window.open("https://drive.google.com/drive/my-drive", "_blank", "noopener,noreferrer");
    toast.info("Carica il file su Google Drive, poi copia il link condivisibile e incollalo qui.");
  };

  return (
    <div className="space-y-3 min-w-0 w-full" data-testid={testId}>
      {value.length > 0 && (
        <ul className="space-y-2 min-w-0">
          {value.map((a, i) => (
            <li key={i} data-testid={`${testId}-item-${i}`} className="flex items-center gap-2 p-2.5 rounded-xl border border-zen-border bg-white min-w-0">
              <FileText size={16} className="text-zen-primary shrink-0"/>
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="text-sm font-medium text-zen-ink truncate">{a.name}</div>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  title={a.url}
                  className="text-xs text-zen-info hover:underline flex items-center gap-1 min-w-0"
                >
                  <ExternalLink size={11} className="shrink-0"/>
                  <span className="truncate block min-w-0 flex-1">{a.url}</span>
                </a>
              </div>
              <button type="button" onClick={()=>removeAt(i)} data-testid={`${testId}-remove-${i}`} className="text-zen-alert hover:opacity-70 shrink-0">
                <Trash2 size={14}/>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
        <Input
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e)=>setName(e.target.value)}
          placeholder="Nome (opz.)"
          className="text-sm"
        />
        <Input
          data-testid={`${testId}-url`}
          value={url}
          onChange={(e)=>setUrl(e.target.value)}
          placeholder="https://drive.google.com/…"
          className="text-sm"
          onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Button type="button" onClick={add} data-testid={`${testId}-add`} variant="outline" className="rounded-full">
          <Plus size={14} className="mr-1"/> Aggiungi
        </Button>
      </div>
      <button
        type="button"
        onClick={openDriveUpload}
        data-testid={`${testId}-drive-upload`}
        className="w-full text-sm text-zen-primary hover:underline flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-zen-primary/30 hover:bg-zen-primary/5 transition-colors"
      >
        <Link2 size={14}/> Carica file su Google Drive e ottieni il link
      </button>
    </div>
  );
}
